import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

// AuthContext — single source of truth for the SPA's auth state.
//
// Lifecycle:
//   • mount: if `radar_session_token` is in localStorage, hit /api/auth/me
//     to learn who we are. Two resulting states:
//       - 'unauthed' → LoginModal
//       - 'ready'    → render the app
//   • If the URL fragment carries `session_id=...` we DO NOT call /me here —
//     `AuthCallback` (mounted by `App.js` synchronously) owns that exchange
//     and writes the resulting session_token back into context.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'radar_session_token';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  // 'checking' on first mount avoids a flash of the login modal while we
  // resolve an existing session. Resolves to 'unauthed' or 'ready'.
  const [status, setStatus] = useState('checking');

  const refresh = useCallback(async (overrideToken) => {
    const t = overrideToken ?? localStorage.getItem(TOKEN_KEY);
    if (!t) { setStatus('unauthed'); setUser(null); return null; }
    try {
      const r = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUser(r.data.user);
      setStatus('ready');
      return r.data;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null); setUser(null); setStatus('unauthed');
      return null;
    }
  }, []);

  const applyToken = useCallback(async (nextToken) => {
    if (nextToken) localStorage.setItem(TOKEN_KEY, nextToken);
    else           localStorage.removeItem(TOKEN_KEY);
    setToken(nextToken);
    return refresh(nextToken);
  }, [refresh]);

  const logout = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) {
      try { await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${t}` },
      }); } catch { /* idempotent */ }
    }
    await applyToken(null);
  }, [applyToken]);

  useEffect(() => {
    // If returning from the OAuth callback, AuthCallback owns the exchange
    // — don't race it with a /me probe.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setStatus('checking');
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = { status, token, user, applyToken, refresh, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export { TOKEN_KEY, API };
